import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { LinePlan } from '@models/asset-details.model';
import { ImageMetaData } from '@models/my-assets.model';
import { UserAsset } from '@models/dashboard.model';
import {
  MULTI_SIM_TYPES,
  carrierLogoUrl,
  constructAssetTitle,
  fieldValueIsPresent,
  generateAssetImageUrl,
  hasDeviceCatalogId,
  hasDeviceId,
  hasLineId
} from '@models/functions.model';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { EligibilityBadgeComponent } from '@shared-components/badge/eligibility-badge/eligibility-badge.component';
import { StatusBadgeComponent } from '@shared-components/badge/status-badge/status-badge.component';

@Component({
  selector: 'ts-dashboard-asset',
  templateUrl: './dashboard-asset.component.html',
  imports: [
    CommonModule,
    NgbAccordionModule,
    TranslationPipe,
    EligibilityBadgeComponent,
    StatusBadgeComponent
  ]
})
export class DashboardAssetComponent {
  @Input() asset: UserAsset;
  @Input() is_detail: boolean = false;
  @Input() plan?: LinePlan | null;
  @Input() is_offcanvas?: boolean;
  @Input() offcanvas_type?: string;
  multi_sim_types: string[] = MULTI_SIM_TYPES;

  hasLine(asset: UserAsset): boolean {
    return hasLineId(asset.line);
  }

  hasDevice(asset: UserAsset): boolean {
    return hasDeviceId(asset.device);
  }

  hasRefDevice(asset: UserAsset): boolean {
    return hasDeviceCatalogId(asset.device);
  }

  getImageMetaData(asset: UserAsset): ImageMetaData {
    if (
      !this.is_offcanvas &&
      [this.multi_sim_types[5], this.multi_sim_types[6]].includes(this.asset.multi_sim_type) &&
      hasDeviceCatalogId(asset.device)
    )
      return {
        src: asset.device.image_url ?? generateAssetImageUrl(asset.device.type),
        alt:
          `${asset.device.manufacturer ?? ''} ${asset.device.model ?? ''}`.trim() || 'Device image'
      };

    if (
      this.is_offcanvas &&
      this.asset.line &&
      [this.multi_sim_types[1], this.multi_sim_types[5], this.multi_sim_types[6]].includes(
        this.offcanvas_type!
      )
    )
      return { src: carrierLogoUrl(this.asset.line.carrier_image_url), alt: this.asset.line.carrier_name ?? '' };

    if (!asset.device?.id)
      return {
        src: '/assets/images/devices/line_placeholder.png',
        alt: asset.line?.international_number ?? 'Line'
      };

    const src: string =
      (this.is_detail
        ? asset.device.image_url?.replace('thumb', 'original')
        : asset.device.image_url) || generateAssetImageUrl(asset.device.type);
    return { src, alt: this.assetName(asset) };
  }

  assetName(asset: UserAsset): string {
    return constructAssetTitle(asset);
  }

  fieldExists(field: string | undefined): string | undefined {
    return fieldValueIsPresent(field);
  }

  imageLoadError(event: any, asset: UserAsset): void {
    if (!asset.device?.type) return;
    event.target.src = generateAssetImageUrl(asset.device.type);
  }
}
